// Mail V2 — メール詳細表示。返信編集は MailV2PageComposer dialog に統一する。
import {
  ArrowLeft,
  Archive,
  AlertOctagon,
  Trash2,
  MailOpen,
  FolderInput,
  ChevronUp,
  ChevronDown,
  Star,
  Reply,
  ReplyAll,
  Forward,
  Sparkles,
  Paperclip,
  MoreVertical,
  Download,
} from "lucide-react";
import { type MailMessage } from "../data/demoMailData";
import { Button } from "../../../components/ui/Button";
import { type MailAttachmentDTO } from "../../../api/mail-v2";
import { MailV2Thread } from "./MailV2Thread";
import { formatJstDateTime as formatJstDateTimeFixed } from "../../../utils/dateTime";

interface MailV2DetailProps {
  message: MailMessage | null;
  disabled?: boolean;
  onOpenAiDrawer: () => void;
  onPrevMessage: () => void;
  onNextMessage: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  positionIndex: number;
  totalCount: number;
  onMarkUnread?: () => void;
  onToggleStar?: () => void;
  onArchive?: () => void;
  onTrash?: () => void;
  onSpam?: () => void;
  onUnspam?: () => void;
  onMoveToInbox?: () => void;
  onPermanentDelete?: () => void;
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
  messageAttachments?: MailAttachmentDTO[];
  messageAttachmentsError?: string | null;
  onDownloadMessageAttachment?: (attachmentId: string) => void;
  thread?: import("../api/mappers").MessageV2View[] | null;
  threadRootId?: string | null;
  threadCurrentId?: string | null;
  onSelectThreadMessage?: (id: string) => void;
}

function formatJstDateTime(iso: string): string {
  const value = formatJstDateTimeFixed(iso);
  return value === "—" ? "" : value.replace(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})$/, "$1年$2月$3日 $4");
}

function formatSize(bytes: number): string {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function normalizeInlineToken(value: string): string {
  const trimmed = value.trim().replace(/^cid:/i, "").replace(/^<|>$/g, "");
  try {
    return decodeURIComponent(trimmed).toLowerCase();
  } catch {
    return trimmed.toLowerCase();
  }
}

function buildInlineAttachmentUrl(id: string): string {
  return `/api/mail-v2/attachments/${id}/download?inline=1`;
}

function isPreviewableImage(attachment: MailAttachmentDTO): boolean {
  return /^image\/(png|jpe?g|gif|webp|svg\+xml)$/i.test(attachment.content_type);
}

function hydrateInlineImages(
  html: string,
  attachments?: MailAttachmentDTO[],
): string {
  if (!html || !attachments || attachments.length === 0 || typeof DOMParser === "undefined") {
    return html;
  }

  const inlineAttachments = attachments.filter((a) => a.is_inline || !!a.content_id);
  if (inlineAttachments.length === 0) return html;

  const byCid = new Map<string, MailAttachmentDTO>();
  const byFilename = new Map<string, MailAttachmentDTO>();
  for (const attachment of inlineAttachments) {
    if (attachment.content_id) byCid.set(normalizeInlineToken(attachment.content_id), attachment);
    if (attachment.filename) byFilename.set(attachment.filename.trim().toLowerCase(), attachment);
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  let changed = false;
  const inlineOnly = inlineAttachments.length === 1 ? inlineAttachments[0] : null;

  doc.querySelectorAll("img").forEach((img) => {
    const rawSrc = img.getAttribute("src") || "";
    const alt = (img.getAttribute("alt") || "").trim().toLowerCase();
    let matched: MailAttachmentDTO | undefined;
    if (/^cid:/i.test(rawSrc)) matched = byCid.get(normalizeInlineToken(rawSrc));
    if (!matched && !rawSrc && alt) matched = byFilename.get(alt);
    if (!matched && !rawSrc && inlineOnly) matched = inlineOnly;
    if (!matched) return;
    img.setAttribute("src", buildInlineAttachmentUrl(matched.id));
    changed = true;
  });

  return changed ? doc.body.innerHTML : html;
}

function normalizeEmailLayout(html: string): string {
  if (!html || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll<HTMLElement>("*").forEach((element) => {
    // Marketing templates often use huge tracking letter-spacing and
    // indentation values that are unreadable inside our responsive pane.
    element.style.removeProperty("letter-spacing");
    element.style.removeProperty("word-spacing");
    element.style.removeProperty("text-indent");
  });
  doc.querySelectorAll("style").forEach((style) => {
    style.textContent = (style.textContent || "")
      .replace(/letter-spacing\s*:[^;}]+[;]?/gi, "")
      .replace(/word-spacing\s*:[^;}]+[;]?/gi, "")
      .replace(/text-indent\s*:[^;}]+[;]?/gi, "");
  });
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (node.nodeValue) {
      node.nodeValue = node.nodeValue
        .replace(/[\u00a0\u202f]+/g, " ")
        // Mail clients frequently export a space between every CJK/full-width
        // character. Remove only those unambiguous inter-character spaces;
        // normal English word spacing remains intact.
        .replace(/([\u3040-\u30ff\u3400-\u9fff\uff00-\uffef])\s+(?=[\u3040-\u30ff\u3400-\u9fff\uff00-\uffef])/g, "$1");
    }
  }
  return doc.body.innerHTML;
}

export function MailV2Detail({
  message,
  disabled = false,
  onOpenAiDrawer,
  onPrevMessage,
  onNextMessage,
  hasPrev,
  hasNext,
  positionIndex,
  totalCount,
  onMarkUnread,
  onToggleStar,
  onArchive,
  onTrash,
  onSpam,
  onUnspam,
  onMoveToInbox,
  onPermanentDelete,
  onReply,
  onReplyAll,
  onForward,
  messageAttachments,
  messageAttachmentsError,
  onDownloadMessageAttachment,
  thread,
  threadRootId,
  threadCurrentId,
  onSelectThreadMessage,
}: MailV2DetailProps) {
  if (!message) {
    return (
      <div className="mv2-detail mv2-empty-state">
        <div className="mv2-empty-state-text">メールを選択してください</div>
      </div>
    );
  }

  const safeSenderName =
    typeof message.senderName === "string" && message.senderName.trim()
      ? message.senderName
      : "(送信者不明)";
  const safeSenderInitial = safeSenderName.charAt(0).toUpperCase();
  const safeRecipients = Array.isArray(message.recipients) ? message.recipients : [];
  const safeCcRecipients = Array.isArray((message as { ccRecipients?: string[] }).ccRecipients)
    ? ((message as { ccRecipients?: string[] }).ccRecipients ?? [])
    : [];
  const safeReplyToRecipients = Array.isArray((message as { replyToRecipients?: string[] }).replyToRecipients)
    ? ((message as { replyToRecipients?: string[] }).replyToRecipients ?? [])
    : [];
  const showReplyToRecipients = safeReplyToRecipients.filter(
    (addr) => addr.trim().toLowerCase() !== String(message.senderEmail ?? "").trim().toLowerCase(),
  );
  const safeHtmlBody = normalizeEmailLayout(hydrateInlineImages(
    typeof message.htmlBody === "string" ? message.htmlBody : "",
    messageAttachments,
  ));
  const hasRichEmailMarkup = /<(table|img|a|h[1-6]|ul|ol|blockquote)\b/i.test(safeHtmlBody);
  const plainBody = typeof (message as { bodyText?: unknown }).bodyText === "string"
    ? (message as unknown as { bodyText: string }).bodyText
    : "";

  const messageBody = (
    <>
      {thread && thread.length > 1 && onSelectThreadMessage && (
        <MailV2Thread
          thread={thread}
          currentId={threadCurrentId ?? message.id}
          rootId={threadRootId ?? null}
          onSelectThreadMessage={onSelectThreadMessage}
        />
      )}
      {thread && thread.length === 1 && (
        <div className="mv2-thread-summary" data-testid="mv2-thread-summary">
          <span>この会話には1件のメールがあります</span>
        </div>
      )}
      <div className="mv2-detail-subject">{message.subject}</div>
      <div className="mv2-detail-header">
        <div className="mv2-detail-avatar">{safeSenderInitial}</div>
        <div className="mv2-detail-meta">
          <div className="mv2-detail-from-row">
            <span className="mv2-detail-sender-name">{safeSenderName}</span>
            <span className="mv2-detail-sender-email">&lt;{message.senderEmail ?? ""}&gt;</span>
          </div>
          <div className="mv2-detail-recipients">
            宛先: {safeRecipients.length > 0 ? safeRecipients.join(", ") : "(未設定)"}
          </div>
          {safeCcRecipients.length > 0 && (
            <div className="mv2-detail-recipients">Cc: {safeCcRecipients.join(", ")}</div>
          )}
          {showReplyToRecipients.length > 0 && (
            <div className="mv2-detail-recipients">
              Reply-To: {showReplyToRecipients.join(", ")}
            </div>
          )}
          <div className="mv2-detail-date">{formatJstDateTime(message.receivedAt)}</div>
        </div>
        <div className="mv2-detail-actions">
          <button
            className="mv2-toolbar-btn"
            title={message.isStarred ? "スター解除" : "スター"}
            aria-label={message.isStarred ? "スター解除" : "スター"}
            onClick={onToggleStar}
          >
            <Star
              size={18}
              fill={message.isStarred ? "#f59e0b" : "none"}
              color={message.isStarred ? "#f59e0b" : "#9ca3af"}
            />
          </button>
          <button className="mv2-toolbar-btn" title="返信" aria-label="返信" onClick={onReply}>
            <Reply size={18} />
          </button>
          <button
            className="mv2-toolbar-btn"
            title="その他は未対応です"
            aria-label="その他 (未対応)"
            disabled
          >
            <MoreVertical size={18} />
          </button>
        </div>
      </div>
      {hasRichEmailMarkup || !plainBody ? (
        <div className="mv2-detail-html" dangerouslySetInnerHTML={{ __html: safeHtmlBody }} />
      ) : (
        <div className="mv2-detail-plain-text">{plainBody.replace(/\r\n/g, "\n").replace(/\r/g, "\n")}</div>
      )}
      <div className="mv2-detail-reply-actions">
        <Button variant="primary" iconLeft={<Reply size={16} />} onClick={onReply}>
          返信
        </Button>
        <Button variant="secondary" iconLeft={<ReplyAll size={16} />} onClick={onReplyAll}>
          全員に返信
        </Button>
        <Button variant="secondary" iconLeft={<Forward size={16} />} onClick={onForward}>
          転送
        </Button>
        <Button
          variant="secondary"
          iconLeft={<Sparkles size={16} />}
          onClick={onOpenAiDrawer}
          title="AI クイック返信を開く"
        >
          AI返信提案
        </Button>
      </div>
      {(messageAttachments && messageAttachments.length > 0) || messageAttachmentsError ? (
        <div className="mv2-message-attachment-list" data-testid="mv2-message-attachment-list">
          <div className="mv2-message-attachment-label">
            添付ファイル ({messageAttachments?.length ?? 0})
          </div>
          {messageAttachmentsError && (
            <div className="mv2-message-attachment-error" role="alert">
              {messageAttachmentsError}
            </div>
          )}
          {messageAttachments && messageAttachments.length > 0 && (
            <>
              <ul className="mv2-message-attachment-chips" role="list">
                {messageAttachments.map((a) => (
                  <li key={a.id} className="mv2-message-attachment-chip" role="listitem" data-testid="mv2-message-attachment-chip">
                    <Paperclip size={12} />
                    <span className="mv2-message-attachment-chip-name" title={a.filename}>{a.filename}</span>
                    <span className="mv2-message-attachment-chip-size">{formatSize(a.size_bytes)}</span>
                    {onDownloadMessageAttachment && (
                      <button type="button" className="mv2-message-attachment-chip-download" aria-label={`${a.filename} をダウンロード`} title="ダウンロード" onClick={() => onDownloadMessageAttachment(a.id)}>
                        <Download size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {messageAttachments.filter(isPreviewableImage).map((a) => (
                <div className="mv2-message-image-preview" key={`preview-${a.id}`}>
                  <img src={buildInlineAttachmentUrl(a.id)} alt={a.filename} loading="lazy" />
                </div>
              ))}
            </>
          )}
        </div>
      ) : null}
    </>
  );

  return (
    <div className="mv2-detail">
      {disabled ? (
        <div className="mv2-list-disabled-banner" data-testid="mv2-detail-repair-banner">
          この mailbox は修復中のため操作できません
        </div>
      ) : null}
      <div className="mv2-detail-toolbar">
        <button className="mv2-toolbar-btn" title="戻る" aria-label="戻る" disabled={!hasPrev} onClick={onPrevMessage}>
          <ArrowLeft size={18} />
        </button>
        <button className="mv2-toolbar-btn" title="アーカイブ" aria-label="アーカイブ" disabled={!onArchive} onClick={onArchive}>
          <Archive size={18} />
        </button>
        <button
          className="mv2-toolbar-btn"
          title={onUnspam ? "受信トレイに戻す" : "迷惑メール"}
          aria-label={onUnspam ? "受信トレイに戻す" : "迷惑メール"}
          disabled={!onSpam && !onUnspam}
          onClick={() => (onUnspam ? onUnspam() : onSpam?.())}
        >
          <AlertOctagon size={18} />
        </button>
        <button
          className="mv2-toolbar-btn"
          title={onPermanentDelete && !onTrash ? "完全削除" : "削除"}
          aria-label={onPermanentDelete && !onTrash ? "完全削除" : "削除"}
          disabled={!onTrash && !onPermanentDelete}
          onClick={() => (onTrash ? onTrash() : onPermanentDelete?.())}
        >
          <Trash2 size={18} />
        </button>
        <button className="mv2-toolbar-btn" title="未読にする" aria-label="未読にする" disabled={!onMarkUnread} onClick={onMarkUnread}>
          <MailOpen size={18} />
        </button>
        {onMoveToInbox ? (
          <button className="mv2-toolbar-btn" title="受信トレイに戻す" aria-label="受信トレイに戻す" onClick={onMoveToInbox}>
            <FolderInput size={18} />
          </button>
        ) : null}
        <div className="mv2-detail-toolbar-spacer" />
        <span className="mv2-detail-toolbar-text">
          {totalCount === 0 ? "0 / 0" : `${positionIndex} / ${totalCount}`}
        </span>
        <button className="mv2-toolbar-btn" onClick={onPrevMessage} disabled={!hasPrev} title="前のメール" aria-label="前のメール">
          <ChevronUp size={18} />
        </button>
        <button className="mv2-toolbar-btn" onClick={onNextMessage} disabled={!hasNext} title="次のメール" aria-label="次のメール">
          <ChevronDown size={18} />
        </button>
      </div>
      <div className="mv2-message-only-scroll">{messageBody}</div>
    </div>
  );
}
